import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Users, FileText, Contact, Clock, Wallet, LogOut,
  Sun, Moon, Menu, Building2, UserCog, ChevronDown, CalendarDays, KeyRound, CircleUser, Briefcase,
} from "lucide-react";
import ChangePassword from "@/components/ChangePassword";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-tab-dashboard", roles: ["hr", "employee"], end: true },
  { to: "/my-space", label: "My Space", icon: CircleUser, testid: "nav-tab-myspace", roles: ["employee"] },
  { to: "/recruitment", label: "Recruitment", icon: Users, testid: "nav-tab-recruitment", roles: ["hr"] },
  { to: "/offers", label: "Offers & Onboarding", icon: FileText, testid: "nav-tab-offers", roles: ["hr"] },
  { to: "/directory", label: "Employee Directory", icon: Contact, testid: "nav-tab-directory", roles: ["hr", "employee"] },
  { to: "/attendance", label: "Attendance", icon: Clock, testid: "nav-tab-attendance", roles: ["hr", "employee"] },
  { to: "/leaves", label: "Leave Management", icon: CalendarDays, testid: "nav-tab-leaves", roles: ["hr", "employee"] },
  { to: "/jobs", label: "Internal Jobs", icon: Briefcase, testid: "nav-tab-jobs", roles: ["hr", "employee"] },
  { to: "/payroll", label: "Payroll & CTC", icon: Wallet, testid: "nav-tab-payroll", roles: ["hr", "employee"] },
  { to: "/relieving", label: "Relieving & Exit", icon: LogOut, testid: "nav-tab-relieving", roles: ["hr", "employee"] },
];

export default function Layout() {
  const { user, logout, viewRole, setViewRole } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const role = viewRole || user.role;

  const items = NAV.filter((n) => n.roles.includes(role));

  const initials = (user.name || user.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background text-foreground font-body">
      {/* Sidebar */}
      <aside
        className={`fixed z-40 md:static inset-y-0 left-0 w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border">
          <div className="w-9 h-9 rounded-lg bg-white overflow-hidden flex items-center justify-center border border-border">
            <img src="/logo.jpg" alt="Skanovix logo" className="w-full h-full object-cover" />
          </div>
          <div className="leading-tight">
            <p className="font-heading font-extrabold text-sm tracking-tight">Skanovix</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Info Tech</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground px-2 mb-1">
            Recruitment → Relieving
          </p>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 bg-card/80 backdrop-blur-md border-b border-border flex items-center gap-3 px-4 md:px-6">
          <button className="md:hidden" onClick={() => setOpen(true)} data-testid="sidebar-toggle">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden sm:block">
            <h1 className="font-heading font-bold text-base tracking-tight">HR Enterprise Suite</h1>
            <p className="text-[11px] text-muted-foreground">Skanovix Info Tech Solutions</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {user.role === "hr" && (
              <div className="hidden sm:flex items-center gap-1 rounded-full border border-border p-0.5 bg-secondary" data-testid="role-switcher-toggle">
                {["hr", "employee"].map((r) => (
                  <button
                    key={r}
                    onClick={() => setViewRole(r)}
                    data-testid={`role-switch-${r}`}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      role === r ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {r === "hr" ? "HR View" : "Employee View"}
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="rounded-full">
              {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-secondary transition-colors" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold">
                    {initials}
                  </div>
                  <div className="hidden sm:block text-left leading-tight">
                    <p className="text-xs font-semibold">{user.name}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">{user.role}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-2">
                  <UserCog className="w-3.5 h-3.5" /> {user.email}
                </div>
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-button">
                  <KeyRound className="w-4 h-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          <ErrorBoundary>
            <Outlet context={{ role }} />
          </ErrorBoundary>
        </main>
        <ChangePassword
          forced={!!user.must_change_password}
          open={pwOpen || !!user.must_change_password}
          onClose={() => setPwOpen(false)}
        />
      </div>
    </div>
  );
}
